import { requestAnalysis } from './analysisRequest';
import { prepareImageForAPI, inspectTransparency } from "./imageUtils";


const WHITE_CHANNEL_MIN = 240;

const isNearWhite = (hex) => {
  if (typeof hex !== "string") return false;

  let value = hex.replace("#", "").trim();
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (value.length !== 6) return false;

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return false;

  return r >= WHITE_CHANNEL_MIN && g >= WHITE_CHANNEL_MIN && b >= WHITE_CHANNEL_MIN;
};

/**
 * Removes a white entry the vision model only "saw" because transparency is
 * composited onto white before the model receives the image.
 *
 * Only drops white when the source actually has a transparent region and no
 * opaque white pixels, so artwork containing real white is left alone. For
 * embroidery a phantom white layer is costly: it stitches a solid block of
 * white thread behind the design.
 */
const dropPhantomWhite = async (colors, base64Image) => {
  if (!colors.some((color) => isNearWhite(color?.hex))) return colors;

  const { hasTransparency, opaqueHasWhite } = await inspectTransparency(base64Image);
  if (!hasTransparency || opaqueHasWhite) return colors;

  const filtered = colors.filter((color) => !isNearWhite(color?.hex));
  if (!filtered.length) return colors;

  console.log("🎨 Dropped white detected from transparent background");
  return filtered;
};

/**
 * AI-powered color detection using Gemini vision model.
 * Returns detected colors with hex codes and metadata.
 * 
 * @param {string} base64Image - The base64 encoded image (with or without data URL prefix)
 * @returns {Promise<{colorCount: number, colors: Array<{name: string, hex: string}>, dominantColor: object, hasGradients: boolean, complexity: string}>}
 */
export const detectImageColorsAI = async (base64Image) => {
  try {
    console.log("🎨 AI Color Detection: Starting analysis...");
    
    const base64String = await prepareImageForAPI(base64Image);
    
    const response = await requestAnalysis(
      'detectImageColors',
      { base64Image: base64String },
      30000
    );
    
    console.log("🎨 AI Color Detection result:", response.data);
    
    return response.data;
    
  } catch (error) {
    console.error("❌ AI Color Detection failed:", error);
    throw error;
  }
};

/**
 * Convert AI color detection result to the format expected by the converter.
 * The converter expects an array of [hexWithoutHash, enabled] tuples.
 * 
 * @param {object} aiResult - Result from detectImageColorsAI
 * @returns {Array<[string, boolean]>} - Array of [hexColor, enabled] tuples
 */
export const convertAIColorsToConverterFormat = (aiResult) => {
  if (!aiResult || !aiResult.colors || aiResult.colors.length === 0) {
    console.warn("🎨 No colors from AI, returning empty array");
    return [];
  }
  
  // Convert from [{name, hex}] to [[hexWithoutHash, true]]
  const converterColors = aiResult.colors.map(color => {
    // Remove # from hex if present
    const hexWithoutHash = color.hex.replace('#', '');
    return [hexWithoutHash, true];
  });
  
  console.log("🎨 Converted colors for converter:", converterColors);
  return converterColors;
};

/**
 * Wrapper that detects colors and returns in converter format.
 * Drop-in replacement for ColorAnalyzer.processImage()
 * 
 * @param {string} base64Image - The base64 encoded image
 * @returns {Promise<Array<string>>} - Array of hex colors (with #)
 */
export const processImageWithAI = async (base64Image) => {
  const result = await detectImageColorsAI(base64Image);
  
  if (!result || !result.colors || result.colors.length === 0) {
    console.warn("🎨 AI returned no colors");
    return [];
  }
  
  const colors = await dropPhantomWhite(result.colors, base64Image);

  // Return array of hex colors (with #) to match ColorAnalyzer.processImage output
  return colors.map(color => color.hex);
};

/**
 * Detects colors AND returns recommended trace settings from AI.
 * Use this in the main upload flow to get optimal settings in one call.
 * 
 * @param {string} base64Image - The base64 encoded image
 * @returns {Promise<{colors: Array<string>, traceSettings: object|null}>}
 */
export const processImageWithAIAndSettings = async (base64Image) => {
  const result = await detectImageColorsAI(base64Image);
  
  const colors = (result?.colors?.length > 0)
    ? (await dropPhantomWhite(result.colors, base64Image)).map(color => color.hex)
    : [];

  const traceSettings = result?.traceSettings || null;

  console.log("🎨 AI trace settings recommendation:", traceSettings);

  return { colors, traceSettings };
};

export default {
  detectImageColorsAI,
  convertAIColorsToConverterFormat,
  processImageWithAI,
  processImageWithAIAndSettings,
};
